import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, CheckCircle2, Info, Loader2, ShieldCheck } from 'lucide-react';
import { verifieerCertificaat } from '../services/beroepsregistratieApi';
import { RegelsTabel } from '../components/BeroepsregistratieRegels';
import { fmtDag, fmtGetal, fmtTijdstip } from '../utils/beroepsregistratieFormat';

const statusVan = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

/** Publieke QR-/linkbestemming van het Belagom Beroepsregistratiecertificaat (geen login). */
export const CertificaatVerificatiePage: React.FC = () => {
  const { hash = '' } = useParams<{ hash: string }>();
  const { t } = useTranslation('pages');
  const schoon = hash.trim().toLowerCase();
  const wellGevormd = /^[0-9a-f]{64}$/.test(schoon);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['certificaat-verificatie', schoon],
    queryFn: () => verifieerCertificaat(schoon),
    enabled: wellGevormd,
    retry: false,
  });

  const httpStatus = statusVan(error);
  const fout = !wellGevormd || httpStatus === 400 ? 'malformed' : httpStatus === 404 ? 'unknown' : isError ? 'error' : null;
  const c = data?.certificaat;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col items-center text-center">
          <img src="/keurmerk-beroepsregistratie.png" alt={t('beroepsregistratie.sealAlt')} className="h-36 w-auto object-contain" width={89} height={144} />
          <div className="mt-3 flex items-center gap-2 text-[#0B1F3A]">
            <ShieldCheck size={22} />
            <h1 className="text-lg font-bold">{t('beroepsregistratie.verify.title')}</h1>
          </div>
          <p className="mt-1 max-w-xl text-xs text-gray-500">{t('beroepsregistratie.verify.subtitle')}</p>
        </div>

        {isLoading && wellGevormd ? (
          <div className="mt-8 flex justify-center text-gray-400" aria-busy="true">
            <Loader2 className="animate-spin" />
          </div>
        ) : fout ? (
          <div className="mx-auto mt-6 flex max-w-xl items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5" role="alert">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-semibold text-red-800">{t(`beroepsregistratie.verify.${fout}Title`)}</p>
              <p className="mt-1 text-xs text-red-700">{t(`beroepsregistratie.verify.${fout}Text`)}</p>
              {schoon && <p className="mt-2 break-all font-mono text-[10px] text-red-400">{schoon}</p>}
            </div>
          </div>
        ) : data && c ? (
          <>
            {data.status === 'geldig' ? (
              <div className="mt-6 flex items-start gap-4 rounded-xl border-2 border-green-300 bg-green-50 p-5" role="status">
                <CheckCircle2 size={32} className="flex-shrink-0 text-green-600" />
                <div>
                  <p className="text-lg font-bold text-green-800">{t('beroepsregistratie.verify.validTitle')}</p>
                  <p className="mt-1 text-sm text-green-700">{t('beroepsregistratie.verify.validText')}</p>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex items-start gap-4 rounded-xl border-2 border-red-300 bg-red-50 p-5" role="alert">
                <Ban size={32} className="flex-shrink-0 text-red-600" />
                <div className="min-w-0">
                  <p className="text-lg font-bold text-red-800">{t('beroepsregistratie.verify.invalidTitle')}</p>
                  <p className="mt-1 text-sm text-red-700">{t('beroepsregistratie.verify.invalidText')}</p>
                  {data.afwijkingen.length > 0 && (
                    <>
                      <p className="mt-3 text-xs font-semibold text-red-800">{t('beroepsregistratie.verify.deviations')}</p>
                      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-red-700">
                        {data.afwijkingen.map((a, i) => (
                          <li key={`${i}-${a}`} className="break-words">{a}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-base font-bold text-gray-800">{c.professional.naam}</p>
                  <p className="text-xs text-gray-500">
                    {[c.professional.bedrijfsnaam, c.professional.kvk_nummer && `KvK ${c.professional.kvk_nummer}`].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  <dt className="text-gray-500">{t('beroepsregistratie.verify.period')}</dt>
                  <dd className="font-semibold text-gray-800">{fmtDag(c.periode.van)} – {fmtDag(c.periode.tot)}</dd>
                  <dt className="text-gray-500">{t('beroepsregistratie.verify.issuedOn')}</dt>
                  <dd className="font-semibold text-gray-800">{fmtTijdstip(c.uitgegeven_op)}</dd>
                  <dt className="text-gray-500">{t('beroepsregistratie.verify.format')}</dt>
                  <dd className="font-semibold text-gray-800">{c.formaat.toUpperCase()}</dd>
                </dl>
              </div>

              {c.professional.rollen.length > 0 && (
                <>
                  <p className="mt-4 text-xs font-semibold text-gray-700">{t('beroepsregistratie.verify.roles')}</p>
                  <ul className="mt-1 list-disc pl-5 text-xs text-gray-600">
                    {c.professional.rollen.map((r) => (
                      <li key={`${r.rol}-${r.register ?? ''}-${r.nummer ?? ''}`}>
                        {r.rol}
                        {r.register ? ` — ${r.register}` : ''}
                        {r.nummer ? ` (${r.nummer})` : ''}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <dl className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <dt className="text-[11px] text-gray-500">{t('beroepsregistratie.peLabel')}</dt>
                  <dd className="text-xl font-bold text-[#0B1F3A]">{t('beroepsregistratie.points', { n: fmtGetal(c.totalen.pe_punten) })}</dd>
                  {c.totalen.per_register.length > 0 && (
                    <dd>
                      <ul className="mt-1 text-[11px] text-gray-600">
                        {c.totalen.per_register.map((r) => (
                          <li key={r.register}>
                            {r.register}: <span className="font-semibold">{fmtGetal(r.punten)}</span>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  )}
                </div>
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <dt className="text-[11px] text-gray-500">{t('beroepsregistratie.intervisionLabel')}</dt>
                  <dd className="text-xl font-bold text-[#0B1F3A]">{t('beroepsregistratie.hours', { n: fmtGetal(c.totalen.intervisie_uren) })}</dd>
                  <dd className="mt-1 text-[11px] text-gray-600">{t('beroepsregistratie.intervisionSource', { count: c.totalen.intervisie_sessies })}</dd>
                </div>
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <dt className="text-[11px] text-gray-500">{t('beroepsregistratie.practiceLabel')}</dt>
                  <dd className="text-xl font-bold text-[#0B1F3A]">{t('beroepsregistratie.hours', { n: fmtGetal(c.totalen.praktijk_uren) })}</dd>
                  <dd className="mt-1 text-[11px] text-gray-600">{t('beroepsregistratie.practiceSource', { count: c.totalen.praktijk_facturen })}</dd>
                </div>
              </dl>

              <p className="mt-6 text-sm font-semibold text-gray-700">{t('beroepsregistratie.verify.rowsTitle', { count: c.regels.length })}</p>
              <div className="mt-2">
                {c.regels.length === 0 ? <p className="text-xs text-gray-500">{t('beroepsregistratie.noRows')}</p> : <RegelsTabel regels={c.regels} />}
              </div>

              <div className="mt-5 rounded-lg bg-gray-50 p-3">
                <p className="text-[11px] font-semibold text-gray-600">{t('beroepsregistratie.verify.hash')}</p>
                <p className="mt-0.5 break-all font-mono text-xs text-gray-800">{c.hash}</p>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
              <Info size={16} className="mt-0.5 flex-shrink-0 text-[#0B1F3A]" />
              <div className="text-xs leading-relaxed text-gray-700">
                <p className="font-semibold text-[#0B1F3A]">{t('beroepsregistratie.verify.auditorTitle')}</p>
                <p className="mt-1">{t('beroepsregistratie.verify.auditorText')}</p>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CertificaatVerificatiePage;
